/**
 * exportInvoicesExcel.js
 *
 * Generates a richly-formatted Excel workbook for the Invoices list.
 * Uses ExcelJS for full styling support (fills, fonts, borders, merged cells).
 */

import ExcelJS from "exceljs";

// ─── Color palette ────────────────────────────────────────────────────────────
const C = {
  navy:         "FF0D2D6E",
  gold:         "FFFFC72C",
  white:        "FFFFFFFF",
  softBlue:     "FFE8F0FB",
  lightGray:    "FFD0D0D0",
  orange:       "FFDE6E11",
  red:          "FFCC2222",
  green:        "FF1A7A38",
  teal:         "FF0E7A8A",
  creditOrange: "FFE07B1A",
  yellowFill:   "FFFFD966",
  redFill:      "FFFF8080",
  greenFill:    "FF92D050",
  tealFill:     "FFB2EBF2",
  tealTint:     "FFE0F7FA",
  goldFill:     "FFFFECB3",
  orangeTint:   "FFFFF3E0",
  navyFill:     "FF1A3A6C",
  statCardBg:   "FFF5F8FF",
  gray:         "FF888888",
  textDark:     "FF222222",
  redDark:      "FF8B0000",
  overdueDark:  "FF7B5A00",
  greenDark:    "FF1A4A1A",
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const solid = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });
const font  = (size, bold, argb) => ({ name: "Arial", size, bold, color: { argb } });

const thinBorder = {
  top:    { style: "thin", color: { argb: C.lightGray } },
  left:   { style: "thin", color: { argb: C.lightGray } },
  bottom: { style: "thin", color: { argb: C.lightGray } },
  right:  { style: "thin", color: { argb: C.lightGray } },
};

const leftMid  = { vertical: "middle", horizontal: "left",   indent: 1 };
const centerMid = { vertical: "middle", horizontal: "center" };
const rightMid = { vertical: "middle", horizontal: "right",  indent: 1 };

// ─── Business helpers ─────────────────────────────────────────────────────────
const normCur = (c) => String(c || "USD").toUpperCase() === "EUR" ? "EUR" : "USD";

const detectType = (inv) => {
  if (inv?.isCreditInvoice || inv?.documentType === "credit") return "Credit Invoice";
  if (Number(inv?.total)     < 0) return "Credit Invoice";
  if (Number(inv?.remaining) < 0) return "Credit Invoice";
  if (Array.isArray(inv?.items) && inv.items.some(
    (i) => Number(i?.qty) < 0 || Number(i?.price) < 0
  )) return "Credit Invoice";
  return "Standard Invoice";
};

const signed = (inv, amount) => {
  const n = Number(amount ?? 0);
  return isNaN(n) ? 0 : detectType(inv) === "Credit Invoice" ? -Math.abs(n) : n;
};

const daysOverdue = (inv) => {
  const dd = inv?.dueDate ? new Date(inv.dueDate) : null;
  if (!dd || isNaN(dd.getTime())) return 0;
  const today = new Date(); today.setHours(0, 0, 0, 0); dd.setHours(0, 0, 0, 0);
  return dd < today ? Math.floor((today - dd) / 86400000) : 0;
};

const fmtDate = (v) => {
  if (!v) return "N/A";
  const d = new Date(v);
  return isNaN(d.getTime()) ? "N/A"
    : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" });
};

const curFmt = (cur) => cur === "EUR" ? '€#,##0.00' : '$#,##0.00';
const pct    = (v, t) => !t ? "0.00" : ((v / t) * 100).toFixed(2);

// ═════════════════════════════════════════════════════════════════════════════
// MAIN EXPORT FUNCTION
// ═════════════════════════════════════════════════════════════════════════════
export const exportInvoicesExcel = async ({
  invoices    = [],
  userDetails = {},
  filename    = "Invoices_Export.xlsx",
  companyName = "Invoices",
}) => {
  const wb = new ExcelJS.Workbook();
  wb.creator = "OUS System";
  wb.created = new Date();

  const ws = wb.addWorksheet("Invoices", {
    pageSetup: { paperSize: 9, orientation: "landscape", fitToPage: true, fitToWidth: 1 },
    views: [{ state: "frozen", ySplit: 10, zoomScale: 100 }],  // 100% zoom, all 13 cols on-screen
  });

  // ── Column definitions  (tight widths so all 13 fit at 100% zoom) ─────────
  // Total ≈ 139 units → ~1100px at 100% zoom — fits standard 1920×1080 screen
  ws.columns = [
    { width: 11 }, // A  Company Name
    { width: 13 }, // B  Order Number
    { width: 10 }, // C  Invoice No.
    { width: 10 }, // D  Invoice Date
    { width: 10 }, // E  Due Date
    { width: 11 }, // F  Invoice Amount
    { width: 13 }, // G  Outstanding Amount
    { width: 10 }, // H  Paid Amount
    { width: 14 }, // I  Description
    { width: 8  }, // J  Status
    { width: 9  }, // K  Days Overdue
    { width: 13 }, // L  Invoice Type
    { width: 7  }, // M  Currency
  ];
  const NCOLS = 13;

  // ══════════════════════════════════════════════════════════════════════════
  // ROW 1 — Title banner (compact)
  // ══════════════════════════════════════════════════════════════════════════
  ws.mergeCells(1, 1, 1, NCOLS);
  const titleCell = ws.getCell(1, 1);
  titleCell.value     = companyName && companyName !== "All" ? companyName : "Invoices";
  titleCell.font      = font(16, true, C.white);
  titleCell.fill      = solid(C.navy);
  titleCell.alignment = { ...leftMid, indent: 2 };
  ws.getRow(1).height = 28;

  // ROW 2 — Gold accent bar (thin)
  ws.mergeCells(2, 1, 2, NCOLS);
  ws.getCell(2, 1).fill = solid(C.gold);
  ws.getRow(2).height   = 4;

  // ROW 3 — small spacer
  ws.getRow(3).height = 6;

  // ══════════════════════════════════════════════════════════════════════════
  // PRE-COMPUTE STAT CARD TOTALS
  // ══════════════════════════════════════════════════════════════════════════
  const today0 = new Date(); today0.setHours(0, 0, 0, 0);
  const totals = {
    USD: { total: 0, remaining: 0, overdue: 0, paid: 0 },
    EUR: { total: 0, remaining: 0, overdue: 0, paid: 0 },
  };
  invoices.forEach((inv) => {
    const cur = normCur(inv.currency);
    const rem = signed(inv, inv.remaining);
    const tot = signed(inv, inv.total);
    const pd  = signed(inv, inv.paid ?? (Math.abs(Number(inv.total || 0)) - Math.abs(Number(inv.remaining || 0))));
    const dd  = inv.dueDate ? new Date(inv.dueDate) : null;
    const isOver = dd && !isNaN(dd) && new Date(dd).setHours(0,0,0,0) < today0.getTime() && Math.abs(rem) > 0;
    totals[cur].total     += tot;
    totals[cur].remaining += rem;
    totals[cur].paid      += pd;
    if (isOver) totals[cur].overdue += rem;
  });

  // ══════════════════════════════════════════════════════════════════════════
  // ROWS 4–8 — Stat Cards (3 side-by-side, compact)
  //   Card 1  cols 1-4  (A–D)  Outstanding
  //   Card 2  cols 5-9  (E–I)  Overdue
  //   Card 3  cols 10-13(J–M)  Paid
  // ══════════════════════════════════════════════════════════════════════════
  const rowHeights = { 4: 18, 5: 24, 6: 22, 7: 14, 8: 14 };
  Object.entries(rowHeights).forEach(([r, h]) => { ws.getRow(+r).height = h; });

  // Fill card background for rows 4-8
  for (let r = 4; r <= 8; r++) {
    for (let c = 1; c <= NCOLS; c++) {
      ws.getCell(r, c).fill = solid(C.statCardBg);
    }
  }

  const CARDS = [
    { sc: 1,  ec: 4,  label: "Outstanding Amount", badge: C.orange, amtColor: C.orange,
      usd: totals.USD.remaining, eur: totals.EUR.remaining,
      usdP: pct(totals.USD.remaining, totals.USD.total),
      eurP: pct(totals.EUR.remaining, totals.EUR.total) },
    { sc: 5,  ec: 9,  label: "Overdue Amount",     badge: C.red,    amtColor: C.red,
      usd: totals.USD.overdue,   eur: totals.EUR.overdue,
      usdP: pct(totals.USD.overdue, totals.USD.total),
      eurP: pct(totals.EUR.overdue, totals.EUR.total) },
    { sc: 10, ec: 13, label: "Paid Amount",         badge: C.green,  amtColor: C.green,
      usd: totals.USD.paid,      eur: totals.EUR.paid,
      usdP: pct(totals.USD.paid, totals.USD.total),
      eurP: pct(totals.EUR.paid, totals.EUR.total) },
  ];

  CARDS.forEach(({ sc, ec, label, badge, amtColor, usd, eur, usdP, eurP }) => {
    // ── Row 4: Badge (NOT stretched — only fills exactly sc column,
    //           color applied to sc cell only, no merge across full width)
    //   We put the colored badge text in just the first 2 cols of the card,
    //   leaving the rest of the card row with the card background (white-ish).
    const badgeEnd = Math.min(sc + 1, ec); // badge spans only 2 cols max
    ws.mergeCells(4, sc, 4, badgeEnd);
    const bc = ws.getCell(4, sc);
    bc.value     = `• ${label}`;
    bc.fill      = solid(badge);
    bc.font      = font(8, true, C.white);
    bc.alignment = leftMid;

    // ── Row 5: USD amount
    ws.mergeCells(5, sc, 5, ec);
    const u5 = ws.getCell(5, sc);
    u5.value     = usd;
    u5.numFmt    = '$#,##0.00';
    u5.font      = font(13, true, amtColor);
    u5.fill      = solid(C.statCardBg);
    u5.alignment = leftMid;

    // ── Row 6: EUR amount
    ws.mergeCells(6, sc, 6, ec);
    const u6 = ws.getCell(6, sc);
    u6.value     = eur;
    u6.numFmt    = '€#,##0.00';
    u6.font      = font(13, true, amtColor);
    u6.fill      = solid(C.statCardBg);
    u6.alignment = leftMid;

    // ── Row 7: USD %
    ws.mergeCells(7, sc, 7, ec);
    const u7 = ws.getCell(7, sc);
    u7.value     = `USD: ${usdP}% of total`;
    u7.font      = font(8, false, C.gray);
    u7.fill      = solid(C.statCardBg);
    u7.alignment = leftMid;

    // ── Row 8: EUR %
    ws.mergeCells(8, sc, 8, ec);
    const u8 = ws.getCell(8, sc);
    u8.value     = `EUR: ${eurP}% of total`;
    u8.font      = font(8, false, C.gray);
    u8.fill      = solid(C.statCardBg);
    u8.alignment = leftMid;
  });

  // Spacer row 9 (before header) — no content needed, height only
  ws.getRow(9).height = 4;

  // ══════════════════════════════════════════════════════════════════════════
  // ROW 10 — Table Header (frozen — ySplit: 10 set at worksheet creation above)
  // ══════════════════════════════════════════════════════════════════════════

  const HDR = [
    "Company Name","Order Number","Invoice No.","Invoice Date","Due Date",
    "Invoice Amount","Outstanding Amount","Paid Amount","Description",
    "Status","Days Overdue","Invoice Type","Currency",
  ];
  const hRow = ws.getRow(10);
  hRow.height = 24;
  HDR.forEach((h, i) => {
    const cell = hRow.getCell(i + 1);
    cell.value     = h;
    cell.fill      = solid(C.navy);
    cell.font      = font(9, true, C.white);
    cell.alignment = centerMid;
    cell.border    = {
      top:    { style: "thin",   color: { argb: C.lightGray } },
      left:   { style: "thin",   color: { argb: C.lightGray } },
      right:  { style: "thin",   color: { argb: C.lightGray } },
      bottom: { style: "medium", color: { argb: C.gold } },
    };
  });

  // ══════════════════════════════════════════════════════════════════════════
  // DATA ROWS  (starting at row 11)
  // ══════════════════════════════════════════════════════════════════════════
  const DATA_START = 11;

  invoices.forEach((inv, idx) => {
    const rn   = DATA_START + idx;
    const row  = ws.getRow(rn);
    row.height = 18;
    const isEven = idx % 2 === 0;
    const bgArgb = isEven ? C.white : C.softBlue;

    const cur      = normCur(inv.currency);
    const invType  = detectType(inv);
    const isCredit = invType === "Credit Invoice";
    const days     = daysOverdue(inv);
    const status   = inv.status || "Not Paid";
    const name     = String(inv.name || "").trim() || userDetails?.[inv.userId]?.name || "N/A";
    const invAmt   = signed(inv, inv.total);
    const outAmt   = signed(inv, inv.remaining);
    const paidAmt  = signed(inv, inv.paid ?? (Math.abs(Number(inv.total || 0)) - Math.abs(Number(inv.remaining || 0))));
    const cf       = curFmt(cur);

    const CELLS = [
      { v: name,                                                     fmt: null, align: centerMid },
      { v: inv.orderNumber || "N/A",                                 fmt: null, align: centerMid },
      { v: inv.invoiceNo   || "N/A",                                 fmt: null, align: centerMid },
      { v: fmtDate(inv.date || inv.invoiceDate || inv.createdAt),    fmt: null, align: centerMid },
      { v: fmtDate(inv.dueDate),                                     fmt: null, align: centerMid },
      { v: invAmt,                                                   fmt: cf,   align: rightMid  },
      { v: outAmt,                                                   fmt: cf,   align: rightMid  },
      { v: paidAmt,                                                  fmt: cf,   align: rightMid  },
      { v: inv.description || "",                                    fmt: null, align: leftMid   },
      { v: status,                                                   fmt: null, align: centerMid },
      { v: days,                                                     fmt: "0",  align: centerMid },
      { v: invType,                                                  fmt: null, align: centerMid },
      { v: cur,                                                      fmt: null, align: centerMid },
    ];

    CELLS.forEach(({ v, fmt, align }, i) => {
      const cell     = row.getCell(i + 1);
      cell.value     = v;
      if (fmt) cell.numFmt = fmt;
      cell.fill      = solid(bgArgb);
      cell.font      = font(9, false, C.textDark);
      cell.border    = thinBorder;
      cell.alignment = align;
    });

    // ── Status pill (col 10)
    const sc = row.getCell(10);
    const sLower = status.toLowerCase();
    if (sLower === "paid") {
      sc.fill = solid(C.green);
      sc.font = font(8, true, C.white);
    } else if (sLower === "partially paid") {
      sc.fill = solid(C.orange);
      sc.font = font(8, true, C.white);
    } else {
      sc.fill = solid(C.red);
      sc.font = font(8, true, C.white);
    }

    // ── Days Overdue (col 11)
    const dc = row.getCell(11);
    if (days > 30)     { dc.fill = solid(C.redFill);    dc.font = font(9, true,  C.redDark);     }
    else if (days >= 1){ dc.fill = solid(C.yellowFill); dc.font = font(9, false, C.overdueDark); }
    else               { dc.fill = solid(C.greenFill);  dc.font = font(9, false, C.greenDark);   }

    // ── Invoice Type (col 12)
    const tc = row.getCell(12);
    if (isCredit) { tc.fill = solid(C.orangeTint); tc.font = font(8, true, C.creditOrange); }
    else          { tc.fill = solid(C.tealTint);   tc.font = font(8, true, C.teal);         }
  });

  // ══════════════════════════════════════════════════════════════════════════
  // COLUMN TOTALS — directly below data (Invoice Amt, Outstanding, Paid)
  // ══════════════════════════════════════════════════════════════════════════
  const DATA_END = DATA_START + invoices.length - 1;

  // Per-currency column sums
  const colSum = (cur, field) => invoices.reduce((acc, inv) => {
    if (normCur(inv.currency) !== cur) return acc;
    const v = field === "total"
      ? signed(inv, inv.total)
      : field === "remaining"
      ? signed(inv, inv.remaining)
      : signed(inv, inv.paid ?? (Math.abs(Number(inv.total || 0)) - Math.abs(Number(inv.remaining || 0))));
    return acc + v;
  }, 0);

  const ctUSDInv  = colSum("USD", "total");
  const ctEURInv  = colSum("EUR", "total");
  const ctUSDOut  = colSum("USD", "remaining");
  const ctEUROUt  = colSum("EUR", "remaining");
  const ctUSDPaid = colSum("USD", "paid");
  const ctEURPaid = colSum("EUR", "paid");

  // Helper: style a totals amount cell
  const applyAmt = (cell, val, fmt, bg, fg, bold) => {
    cell.value = val; cell.numFmt = fmt;
    cell.fill = solid(bg); cell.font = font(9, bold, fg);
    cell.alignment = rightMid; cell.border = thinBorder;
  };

  // Helper: merged label cell
  const applyLbl = (rn, cs, ce, text, bg, fg, bold) => {
    ws.mergeCells(rn, cs, rn, ce);
    const c = ws.getCell(rn, cs);
    c.value = text; c.fill = solid(bg);
    c.font = font(9, bold, fg); c.alignment = leftMid; c.border = thinBorder;
  };

  // ── Column Totals header bar (immediately after last data row) ────────────
  const CT = DATA_END + 1;
  ws.getRow(CT).height = 20;
  ws.mergeCells(CT, 1, CT, NCOLS);
  const ctH = ws.getCell(CT, 1);
  ctH.value = "COLUMN TOTALS  (Invoice Amount  |  Outstanding Amount  |  Paid Amount)";
  ctH.fill  = solid(C.navy);  ctH.font = font(9, true, C.white);
  ctH.alignment = { ...leftMid, indent: 2 };
  ctH.border = { bottom: { style: "medium", color: { argb: C.gold } } };

  // ── USD totals row ────────────────────────────────────────────────────────
  const USD_R = CT + 1;
  ws.getRow(USD_R).height = 19;
  applyLbl(USD_R, 1, 5, "USD  –  Row Totals", "FFE3EEF9", C.navy, true);
  applyAmt(ws.getCell(USD_R, 6),  ctUSDInv,  '$#,##0.00', "FFE3EEF9", C.navy,   true);
  applyAmt(ws.getCell(USD_R, 7),  ctUSDOut,  '$#,##0.00', "FFE3EEF9", C.orange, true);
  applyAmt(ws.getCell(USD_R, 8),  ctUSDPaid, '$#,##0.00', "FFE3EEF9", C.green,  true);
  ws.mergeCells(USD_R, 9, USD_R, NCOLS);
  const uFillerCell = ws.getCell(USD_R, 9);
  uFillerCell.fill = solid("FFE3EEF9"); uFillerCell.border = thinBorder;

  // ── EUR totals row ────────────────────────────────────────────────────────
  const EUR_R = CT + 2;
  ws.getRow(EUR_R).height = 19;
  applyLbl(EUR_R, 1, 5, "EUR  –  Row Totals", "FFFFF4E6", C.navy, true);
  applyAmt(ws.getCell(EUR_R, 6),  ctEURInv,  '€#,##0.00', "FFFFF4E6", C.navy,   true);
  applyAmt(ws.getCell(EUR_R, 7),  ctEUROUt,  '€#,##0.00', "FFFFF4E6", C.orange, true);
  applyAmt(ws.getCell(EUR_R, 8),  ctEURPaid, '€#,##0.00', "FFFFF4E6", C.green,  true);
  ws.mergeCells(EUR_R, 9, EUR_R, NCOLS);
  const eFillerCell = ws.getCell(EUR_R, 9);
  eFillerCell.fill = solid("FFFFF4E6"); eFillerCell.border = thinBorder;

  // ── Grand Total USD row (navy, gold accent) ───────────────────────────────────
  const GT_USD = CT + 3;
  ws.getRow(GT_USD).height = 22;
  // Gold left stripe col 1
  ws.getCell(GT_USD, 1).fill = solid(C.gold);
  ws.getCell(GT_USD, 1).border = thinBorder;
  // Label in cols 2-5
  applyLbl(GT_USD, 2, 5, "Grand Total (USD)", C.navyFill, C.gold, true);
  // Values under F, G, H (cols 6, 7, 8)
  applyAmt(ws.getCell(GT_USD, 6),  ctUSDInv,  '$#,##0.00', C.navyFill, C.gold, true);
  applyAmt(ws.getCell(GT_USD, 7),  ctUSDOut,  '$#,##0.00', C.navyFill, C.gold, true);
  applyAmt(ws.getCell(GT_USD, 8),  ctUSDPaid, '$#,##0.00', C.navyFill, C.gold, true);
  // filler cols 9-13
  ws.mergeCells(GT_USD, 9, GT_USD, NCOLS);
  const gtUSDListFiller = ws.getCell(GT_USD, 9);
  gtUSDListFiller.fill = solid(C.navyFill); gtUSDListFiller.border = thinBorder;

  // ── Grand Total EUR row (navy, gold accent) ───────────────────────────────────
  const GT_EUR = CT + 4;
  ws.getRow(GT_EUR).height = 22;
  // Gold left stripe col 1
  ws.getCell(GT_EUR, 1).fill = solid(C.gold);
  ws.getCell(GT_EUR, 1).border = thinBorder;
  // Label in cols 2-5
  applyLbl(GT_EUR, 2, 5, "Grand Total (EUR)", C.navyFill, C.gold, true);
  // Values under F, G, H (cols 6, 7, 8)
  applyAmt(ws.getCell(GT_EUR, 6),  ctEURInv,  '€#,##0.00', C.navyFill, C.gold, true);
  applyAmt(ws.getCell(GT_EUR, 7),  ctEUROUt,  '€#,##0.00', C.navyFill, C.gold, true);
  applyAmt(ws.getCell(GT_EUR, 8),  ctEURPaid, '€#,##0.00', C.navyFill, C.gold, true);
  // filler cols 9-13
  ws.mergeCells(GT_EUR, 9, GT_EUR, NCOLS);
  const gtEURListFiller = ws.getCell(GT_EUR, 9);
  gtEURListFiller.fill = solid(C.navyFill); gtEURListFiller.border = thinBorder;

  // ══════════════════════════════════════════════════════════════════════════
  // TYPE-SPLIT TOTALS SECTION
  // ══════════════════════════════════════════════════════════════════════════
  const TS = GT_EUR + 2;

  const sumTC = (type, cur) => invoices.reduce((acc, inv) => {
    if (normCur(inv.currency) !== cur || detectType(inv) !== type) return acc;
    return acc + signed(inv, inv.total);
  }, 0);

  const stdUSD = sumTC("Standard Invoice", "USD");
  const stdEUR = sumTC("Standard Invoice", "EUR");
  const crdUSD = -Math.abs(sumTC("Credit Invoice", "USD"));
  const crdEUR = -Math.abs(sumTC("Credit Invoice", "EUR"));
  const netUSD = stdUSD + crdUSD;
  const netEUR = stdEUR + crdEUR;

  const shRow = ws.getRow(TS);
  shRow.height = 22;
  ws.mergeCells(TS, 1, TS, NCOLS);
  const shCell = ws.getCell(TS, 1);
  shCell.value     = "INVOICE TOTALS  ·  Split by Type & Currency";
  shCell.fill      = solid(C.navy);
  shCell.font      = font(10, true, C.white);
  shCell.alignment = { ...leftMid, indent: 2 };
  shCell.border    = { bottom: { style: "medium", color: { argb: C.gold } } };

  const TROWS = [
    { label: "Subtotal – Standard Invoices (USD)", value: stdUSD, fmt: '$#,##0.00', bg: C.tealFill,  fg: C.teal,        bold: false, stripe: false },
    { label: "Subtotal – Standard Invoices (EUR)", value: stdEUR, fmt: '€#,##0.00', bg: C.tealFill,  fg: C.teal,        bold: false, stripe: false },
    { label: "Subtotal – Credit Invoices (USD)",   value: crdUSD, fmt: '$#,##0.00', bg: C.goldFill,  fg: C.creditOrange,bold: false, stripe: false },
    { label: "Subtotal – Credit Invoices (EUR)",   value: crdEUR, fmt: '€#,##0.00', bg: C.goldFill,  fg: C.creditOrange,bold: false, stripe: false },
    { label: "Grand Total – Net (USD)",             value: netUSD, fmt: '$#,##0.00', bg: C.navyFill,  fg: C.white,       bold: true,  stripe: true  },
    { label: "Grand Total – Net (EUR)",             value: netEUR, fmt: '€#,##0.00', bg: C.navyFill,  fg: C.white,       bold: true,  stripe: true  },
  ];

  TROWS.forEach(({ label, value, fmt, bg, fg, bold, stripe }, i) => {
    const rn  = TS + 1 + i;
    const row = ws.getRow(rn);
    row.height = 20;

    if (stripe) {
      ws.getCell(rn, 1).fill   = solid(C.gold);
      ws.getCell(rn, 1).border = thinBorder;
    }

    const ls = stripe ? 2 : 1;
    ws.mergeCells(rn, ls, rn, 9);
    const lc = ws.getCell(rn, ls);
    lc.value = label; lc.fill = solid(bg);
    lc.font = font(9, bold, fg); lc.alignment = leftMid; lc.border = thinBorder;

    ws.mergeCells(rn, 10, rn, NCOLS);
    const vc = ws.getCell(rn, 10);
    vc.value = value; vc.numFmt = fmt; vc.fill = solid(bg);
    vc.font = font(10, true, fg); vc.alignment = rightMid;
    vc.border = stripe
      ? { ...thinBorder, top: { style: "medium", color: { argb: C.gold } }, bottom: { style: "medium", color: { argb: C.gold } } }
      : thinBorder;
  });

  // ══════════════════════════════════════════════════════════════════════════
  // Column widths: already set above to tight values for on-screen fit.
  // Only widen if content is unexpectedly long (description column).
  // ══════════════════════════════════════════════════════════════════════════
  const FIXED_WIDTHS = [11, 13, 10, 10, 10, 11, 13, 10, 14, 8, 9, 13, 7];
  ws.columns.forEach((col, i) => {
    const base = FIXED_WIDTHS[i] || 10;
    let max = base;
    col.eachCell({ includeEmpty: false }, (cell) => {
      // Only measure text cells (not number/formula cells) to avoid blowing up widths
      const v = cell.value;
      if (typeof v === "string" && v.length > max) max = Math.min(v.length + 2, base + 4);
    });
    col.width = max;
  });

  // ══════════════════════════════════════════════════════════════════════════
  // Download
  // ══════════════════════════════════════════════════════════════════════════
  const buf  = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a   = document.createElement("a");
  a.href    = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};
